import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import FilterPanel from "./components/FilterPanel";
import ComicCard from "@/components/feature/ComicCard";
import { comics } from "@/mocks/comics";

const PAGE_SIZE = 8;

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const selectedGenres = useMemo(
    () => (searchParams.get("genre") || "").split(",").filter(Boolean),
    [searchParams],
  );
  const status = searchParams.get("status") || "all";
  const sort = searchParams.get("sort") || "popular";

  const updateParams = (next: {
    genres?: string[];
    status?: string;
    sort?: string;
  }) => {
    const params = new URLSearchParams();
    const genresValue = next.genres ?? selectedGenres;
    const statusValue = next.status ?? status;
    const sortValue = next.sort ?? sort;
    if (genresValue.length) params.set("genre", genresValue.join(","));
    if (statusValue && statusValue !== "all") params.set("status", statusValue);
    if (sortValue && sortValue !== "popular") params.set("sort", sortValue);
    setSearchParams(params, { replace: true });
    setVisible(PAGE_SIZE);
  };

  const toggleGenre = (genre: string) => {
    const next = selectedGenres.includes(genre)
      ? selectedGenres.filter((item) => item !== genre)
      : [...selectedGenres, genre];
    updateParams({ genres: next });
  };

  const reset = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
    setVisible(PAGE_SIZE);
    setMobileFilterOpen(false);
  };

  const filtered = useMemo(() => {
    const list = comics.filter((comic) => {
      const genreMatch =
        selectedGenres.length === 0 ||
        selectedGenres.some((genre) => comic.genres.includes(genre));
      const statusMatch = status === "all" || comic.status === status;
      return genreMatch && statusMatch;
    });

    return list.sort((a, b) => {
      if (sort === "updated") {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }
      if (sort === "favorites") {
        return b.favorites - a.favorites;
      }
      return b.views - a.views;
    });
  }, [selectedGenres, status, sort]);

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const categoryName = selectedGenres.length ? selectedGenres.join("、") : "全部漫画";
  const statusText =
    status === "completed" ? "已完结" : status === "serializing" ? "连载中" : "全部状态";
  const sortText = sort === "updated" ? "按更新时间" : sort === "favorites" ? "按收藏量" : "按人气";

  useEffect(() => {
    document.title = `${categoryName} - JMcomic2漫画分类页`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        `${categoryName}分类下共有 ${filtered.length} 部高清漫画作品，支持${sortText}排序、${statusText}筛选，JMcomic2 为你提供流畅的在线漫画阅读体验。`,
      );
    }
  }, [categoryName, filtered.length, sortText, statusText]);

  return (
    <div className="w-full px-4 md:px-6 py-8">
      <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-6 flex-wrap">
        <Link to="/" className="hover:text-primary-500 transition-colors">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link to="/category" className="hover:text-primary-500 transition-colors">
          分类
        </Link>
        {selectedGenres.length > 0 && (
          <>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-950">{selectedGenres.join("、")}</span>
          </>
        )}
      </nav>

      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          {categoryName}
        </h1>
        <p className="mt-2 text-sm text-foreground-500 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>
            当前筛选：
            <span className="text-primary-500">{statusText}</span>
            <span className="mx-1.5 text-foreground-300">·</span>
            <span className="text-primary-500">{sortText}</span>
          </span>
          <span>共 {filtered.length} 部作品</span>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <aside className="hidden lg:block">
          <div className="sticky top-20">
            <FilterPanel
              selectedGenres={selectedGenres}
              status={status}
              sort={sort}
              onToggleGenre={toggleGenre}
              onStatusChange={(value) => updateParams({ status: value })}
              onSortChange={(value) => updateParams({ sort: value })}
              onReset={reset}
            />
          </div>
        </aside>

        <div>
          <div className="lg:hidden mb-4">
            <button
              type="button"
              onClick={() => setMobileFilterOpen((open) => !open)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-950 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-filter-3-line text-primary-500" />
                筛选与排序
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${mobileFilterOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileFilterOpen && (
              <div className="mt-3 animate-fade-in">
                <FilterPanel
                  selectedGenres={selectedGenres}
                  status={status}
                  sort={sort}
                  onToggleGenre={toggleGenre}
                  onStatusChange={(value) => updateParams({ status: value })}
                  onSortChange={(value) => updateParams({ sort: value })}
                  onReset={reset}
                />
              </div>
            )}
          </div>

          {shown.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                {shown.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
              {hasMore ? (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((count) => count + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 px-8 py-3 rounded-md bg-background-100 border border-background-300 text-foreground-950 text-sm font-medium hover:border-primary-400 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-down-line" />
                    加载更多（剩余 {filtered.length - visible} 部）
                  </button>
                </div>
              ) : (
                <p className="mt-8 text-center text-xs text-foreground-500">
                  已经到底啦，共 {filtered.length} 部作品
                </p>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 rounded-lg bg-background-100 border border-background-200">
              <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-foreground-500 mb-4">
                <i className="ri-emotion-sad-line text-3xl" />
              </span>
              <p className="text-foreground-950 font-medium">没有找到符合条件的漫画</p>
              <p className="mt-1 text-sm text-foreground-500">试试调整或重置筛选条件吧</p>
              <button
                type="button"
                onClick={reset}
                className="mt-5 inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}